import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useLiveToolSession } from '../useLiveToolSession';

export interface LiveRankedItem {
  id: string;
  name: string;
  rank: number;
  /** What earned the rank, as it reads on the row: "3 wins", "CoD 12,400". */
  detail: string;
}

interface LiveToolCompleteProps {
  /** "All comparisons complete", "All items scored". */
  heading: string;
  items: LiveRankedItem[];
  /** Back to the scoring screen to change a score before submitting. */
  onChangeScores?: () => void;
}

/**
 * What a live tool shows once a participant has finished: their ranking, and
 * which button sends it. Pairwise, Cost of Delay, WSJF, RICE and Pareto all
 * end here. Before, the four scoring tools stayed on the last item when its
 * "Review" button was pressed, so the button appeared to do nothing.
 */
export function LiveToolComplete({ heading, items, onChangeScores }: LiveToolCompleteProps) {
  // Name the button that is actually rendered — it says "Review & submit" when
  // a rationale is requested and "Submit my response" when it submits directly.
  const { completeLabel } = useLiveToolSession();
  return (
    <div data-testid="live-tool-complete">
      <div className="flex items-center gap-2 mb-4 text-alpine-green-400">
        <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
        <span className="font-body text-[1.02rem]">{heading}</span>
      </div>
      <p className="font-body text-[0.92rem] text-[var(--text-ter)] mb-5 leading-relaxed">
        Use <strong className="text-[var(--text-sec)] font-medium">{completeLabel}</strong> below to send your ranking to the facilitator.
      </p>
      <div className="space-y-2">
        {items.map(item => (
          <div
            key={item.id}
            className="flex items-center gap-3 border border-[var(--rule-bright)] bg-[var(--surface)] px-3.5 py-3"
          >
            <span className="font-heading text-[0.78rem] w-6 text-alpine-green-400 font-bold">{item.rank}</span>
            <span className="font-body text-[0.95rem] text-[var(--text-sec)] flex-1 min-w-0 truncate">{item.name}</span>
            <span className="font-heading text-[0.72rem] text-[var(--text-ter)]">{item.detail}</span>
          </div>
        ))}
      </div>
      {onChangeScores && (
        <button
          type="button"
          onClick={onChangeScores}
          className="mt-5 inline-flex items-center gap-1 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Change my scores
        </button>
      )}
    </div>
  );
}
